import { format } from "date-fns";
import { Avatar, Button, Icon, Typography } from "@/components/atoms";
import { useGuestBooking } from "@/components/pages/Guest/GuestBookingContext";
import { useAppSelector } from "@/redux/hooks";
import {
  selectBookingExtras,
  selectBookingPassenger,
  selectBookingTransferType,
  selectBookingVehicle,
  selectEditJobId,
  selectGuestContact,
  selectNumOfBags,
  selectNumOfPassengers,
  selectPickupDatetime,
} from "@/redux/slices/clientBooking/selectors";
import { clsx } from "@/utils";
import { useAccountHolder } from "./useAccountHolder";
import { capacityLabel, extrasLabel } from "./vehicleTransfer";

export type TripDetailItem = "passenger" | "datetime" | "vehicle" | "other";

/** The Trip Details sub-steps in flow order — the sequence auto-advance walks after each commit. */
export const TRIP_DETAIL_ORDER: TripDetailItem[] = ["passenger", "datetime", "vehicle", "other"];

/**
 * The sub-step that follows `item`, or `item` itself when it's the last one ("other"). Used to
 * auto-advance the open sub-step once the current one's save/proceed succeeds; never advances past
 * the final step.
 */
export const nextTripDetail = (item: TripDetailItem): TripDetailItem => {
  const idx = TRIP_DETAIL_ORDER.indexOf(item);
  return idx >= 0 && idx < TRIP_DETAIL_ORDER.length - 1 ? TRIP_DETAIL_ORDER[idx + 1] : item;
};

interface TripDetailsPanelProps {
  /** The row whose sub-panel is currently open, or `null` when nothing is selected (edit mode start). */
  active: TripDetailItem | null;
  onSelect: (item: TripDetailItem) => void;
  onPrevious: () => void;
  onNext: () => void;
  /** Per-row blockers (from getTripDetailErrors). A non-empty array marks that row incomplete. */
  errors: Record<TripDetailItem, string[]>;
  /** Whether to surface `errors` as danger row states — set once the user has tried to leave the step. */
  showErrors: boolean;
  className?: string;
}

/**
 * A single selectable row. Default: neutral-surface-gray fill + mid-gray border, dark-gray text.
 * Active: primary-light fill with primary border + text. Error takes precedence over active so an
 * incomplete row stays visibly danger even while its sub-panel is open (the open panel already
 * signals "active"). The arrow stays dark gray in every state.
 */
const Row = ({
  active,
  error,
  isEdit,
  onClick,
  children,
}: React.PropsWithChildren<{ active: boolean; error: boolean; isEdit: boolean; onClick: () => void }>) => (
  <button
    type="button"
    onClick={onClick}
    className={clsx(
      "flex w-full items-center gap-3 rounded-lg border p-4 text-left",
      error ? "border-danger bg-danger-light" : active ? "border-primary bg-primary-light" : "border-neutral-mid-gray bg-neutral-surface-gray"
    )}
  >
    <span className="flex min-w-0 flex-1 items-center gap-3">{children}</span>
    {isEdit ? (
      <Icon name="Edit2" size="lg" className="shrink-0 text-neutral-dark-gray" />
    ) : (
      <Icon name="ArrowRight2" size="sm" className="shrink-0 text-neutral-dark-gray" />
    )}
  </button>
);

const Stacked = ({
  title,
  subtitle,
  detail,
  active,
  error,
}: {
  title: string;
  subtitle: string;
  detail?: string;
  active: boolean;
  /** Incomplete row: text turns danger to match the danger box. The specific missing field(s) are
   * surfaced inside the sub-panel (forceValidate), not repeated here. */
  error?: boolean;
}) => {
  const color = error ? "text-danger" : active ? "text-primary" : "text-neutral-dark-gray";
  return (
    <span className="min-w-0">
      <Typography variant="title" className={clsx("block truncate", color)}>
        {title}
      </Typography>
      <Typography variant="paragraph" className={clsx("mt-1 block truncate", color)}>
        {subtitle}
      </Typography>
      {detail && (
        <Typography variant="paragraph" className={clsx("mt-1 block truncate", color)}>
          {detail}
        </Typography>
      )}
    </span>
  );
};

/**
 * The "Trip Details" list — step 2 of the client booking flow. Occupies the same floating slot as
 * the Address panel. Each row selects a sub-step (Who / Date & Time / Vehicle / Other) rendered in
 * TripDetailSubPanel beside it. Content is placeholder for this pass.
 */
export const TripDetailsPanel = ({ active, onSelect, onPrevious, onNext, errors, showErrors, className }: TripDetailsPanelProps) => {
  const { isGuest } = useGuestBooking();
  // A row is flagged danger only after an attempted exit (showErrors) and while it has blockers.
  const hasError = (item: TripDetailItem): boolean => showErrors && errors[item].length > 0;
  const passenger = useAppSelector(selectBookingPassenger);
  const isEdit = useAppSelector(selectEditJobId) != null;
  const accountHolder = useAccountHolder();
  const guestContact = useAppSelector(selectGuestContact);
  // `null` selection ⇒ the account holder ("You") is the passenger by default.
  const isSelf = !passenger || passenger.kind === "self";
  // "First Last", trimmed; empty when neither part is set (never "undefined undefined").
  const guestName = `${guestContact?.firstName ?? ""} ${guestContact?.lastName ?? ""}`.trim();
  const selfName = `${accountHolder.firstName} ${accountHolder.lastName}`.trim();

  // Guest flow: named from the entered contact details (no "(You)"); placeholder until they're set.
  // Authed self: "{name}{selfSuffix}" — "(You)" in self-service, plain client name on an on-behalf booking.
  let passengerName: string;
  let passengerPhone: string;
  if (isGuest) {
    passengerName = guestName || "Passenger Details";
    passengerPhone = guestName ? guestContact?.phone ?? "" : "Enter the passenger's contact details";
  } else if (isSelf) {
    passengerName = selfName ? `${selfName}${accountHolder.selfSuffix}` : "Passenger Details";
    passengerPhone = selfName ? accountHolder.mobile : "Enter the passenger's contact details";
  } else {
    passengerName = `${passenger.firstName} ${passenger.lastName} (Guest)`;
    passengerPhone = passenger.phone;
  }
  // Guests have no account photo — show the generic profile icon instead of the Avatar.
  const showAvatar = !isGuest && isSelf;

  const pickupDatetime = useAppSelector(selectPickupDatetime);
  const pickup = pickupDatetime ? new Date(pickupDatetime) : null;
  const dateTitle = pickup ? format(pickup, "d MMMM yyyy") : "Date and Time Pickup";
  const dateSubtitle = pickup ? format(pickup, "h:mm a") : "Select a date and time for pickup";

  const vehicle = useAppSelector(selectBookingVehicle);
  const transferType = useAppSelector(selectBookingTransferType);
  const numOfPassengers = useAppSelector(selectNumOfPassengers);
  const numOfBags = useAppSelector(selectNumOfBags);
  const extras = useAppSelector(selectBookingExtras);
  // Once committed: transfer type as the title, "Vehicle (N Pax & N Bag)" as the subtitle, and the
  // chosen extras beneath.
  const vehicleTitle = transferType?.label ?? "Vehicle and Transfer";
  const vehicleSubtitle = vehicle
    ? `${vehicle.name} (${capacityLabel(numOfPassengers ?? 1, numOfBags ?? 0)})`
    : "Select vehicle and transfer type";
  const vehicleExtras = vehicle ? extrasLabel(extras) || undefined : undefined;

  return (
    <div
      className={clsx(
        // Mobile: full-bleed white page. md+: floating card over the map.
        "flex flex-col bg-white md:rounded-2xl md:border md:border-neutral-mid-gray md:shadow-lg",
        className
      )}
    >
      {/* Scrollable content so the footer stays pinned even when the viewport is short. */}
      <div className="min-h-0 flex-1 overflow-y-auto p-5">
        <Typography variant="h2">Trip Details</Typography>
        <Typography variant="muted" className="mt-1 block text-neutral-dark-gray">
          Provide the necessary details to complete your booking
        </Typography>

        {/* 24px below the description; rows are 16px apart. */}
        <div className="mt-6 flex flex-col gap-4">
          <Row isEdit={isEdit} active={active === "passenger"} error={hasError("passenger")} onClick={() => onSelect("passenger")}>
            {showAvatar ? (
              <Avatar size="sm" src={accountHolder.avatar} alt={passengerName} />
            ) : (
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-neutral-surface-gray">
                <Icon name="Profile" size="sm" variant="Bold" className="text-neutral-silver-gray" />
              </span>
            )}
            <Stacked title={passengerName} subtitle={passengerPhone} active={active === "passenger"} error={hasError("passenger")} />
          </Row>
          <Row isEdit={isEdit} active={active === "datetime"} error={hasError("datetime")} onClick={() => onSelect("datetime")}>
            <Stacked title={dateTitle} subtitle={dateSubtitle} active={active === "datetime"} error={hasError("datetime")} />
          </Row>
          <Row isEdit={isEdit} active={active === "vehicle"} error={hasError("vehicle")} onClick={() => onSelect("vehicle")}>
            <Stacked title={vehicleTitle} subtitle={vehicleSubtitle} detail={vehicleExtras} active={active === "vehicle"} error={hasError("vehicle")} />
          </Row>
          <Row isEdit={isEdit} active={active === "other"} error={hasError("other")} onClick={() => onSelect("other")}>
            <Stacked title="Other Optional Details" subtitle="Passenger, Notes, and Others" active={active === "other"} error={hasError("other")} />
          </Row>
        </div>
      </div>

      {/* Pinned at the bottom (shrink-0). Divider on mobile only (md+ card has no footer divider). */}
      <div className="flex shrink-0 items-center justify-between gap-3 border-t border-neutral-mid-gray p-4 md:border-t-0">
        <Button variant="secondary" size="md" onClick={onPrevious}>
          Previous Step
        </Button>
        <Button variant="primary" size="md" onClick={onNext}>
          Next Step
        </Button>
      </div>
    </div>
  );
};
